import { memo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FormBuilderField } from '../FormBuilderField';
import { FormBuilderFieldsEmpty } from '../FormBuilderFieldsEmpty';
import { useFormBuilderFields } from '../hooks';
import styles from './FormBuilderFields.module.css';
import { useDropZone } from '@/hooks/useDropZone';
import { EFormBuilderFieldType } from '@/types';
import { isValueOfEnum } from '@/utils/isValueOfEnum';

type TProps = {
  additionalFields?: ReactNode;
  emptyBlock: {
    title: string;
    description: string;
  };
};

export const FormBuilderFields =
  /**
   * Container component for form builder fields with drag-and-drop functionality
   * @param {TProps} props - Component props
   * @param {ReactNode} [props.additionalFields] - Additional fields to render after form fields
   * @param {object} props.emptyBlock - Configuration for empty state
   * @param {string} props.emptyBlock.title - Title to show when no fields exist
   * @param {string} props.emptyBlock.description - Description to show when no fields exist
   * @returns {JSX.Element} Form builder fields container component
   */
  memo(({ additionalFields, emptyBlock }: TProps) => {
    const { t } = useTranslation();
    const { fields, addField } = useFormBuilderFields();
    const { handleDragOver, handleDrop } = useDropZone((key: string) => {
      if (!isValueOfEnum(key, EFormBuilderFieldType))
        throw new Error('Bad key');
      addField(key);
    });

    return (
      <div
        className={styles.container}
        role="group"
        aria-label={t('form-builder.form-elements-drop-zone')}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        {!fields.length ? (
          <FormBuilderFieldsEmpty {...emptyBlock} />
        ) : (
          <>
            {fields.map((field, index) => (
              <FormBuilderField key={field.id} field={field} index={index} />
            ))}
            {additionalFields}
          </>
        )}
      </div>
    );
  });

FormBuilderFields.displayName = 'FormBuilderFields';
